import { T } from '@territory/tokens';

/*
 * The shop storefront buttons (apps/shop legacy .btn-solid / .btn-ghost and .buy.live / .buy.pending), for Button
 * variant="shop" and variant="shop-buy". Verbatim from the shipped apps/shop/src/styles.css so the port is a visual
 * no-op. The sheet paints everything (the inline style carries only the caller `style`), so hover, active and the
 * ::after sheen and arrow can replace values. Shop-only fills are the --shop-* roles in products.shop; outside the
 * shop each role falls back to a base token or to no paint.
 */

/* Every --shop-* role these looks read. products.shop.roles.dark must set each one (consoleRoles test pattern). */
export const SHOP_BUTTON_ROLES = [
  'cta-top',
  'cta-gloss',
  'buy-gloss',
  'cta-shadow',
  'cta-sheen',
  'ghost-fill',
  'buy-glow',
  'shadow-amber',
] as const;
export type ShopButtonRole = (typeof SHOP_BUTTON_ROLES)[number];

export const shopRole = (name: ShopButtonRole, fallback: string): string => `var(--shop-${name}, ${fallback})`;

const NO_SHADOW = '0 0 0 transparent';
const C = {
  top: shopRole('cta-top', T.amber),
  gloss: shopRole('cta-gloss', 'transparent'),
  buyGloss: shopRole('buy-gloss', 'transparent'),
  shadow: shopRole('cta-shadow', NO_SHADOW),
  sheen: shopRole('cta-sheen', 'transparent'),
  ghost: shopRole('ghost-fill', T.bgElev),
  buyGlow: shopRole('buy-glow', NO_SHADOW),
  hoverGlow: shopRole('shadow-amber', NO_SHADOW),
};
const amberFill = `linear-gradient(180deg, ${C.top}, ${T.amber})`;
const ms = (props: string[], s = 0.25): string => props.map((p) => `${p} ${s}s ${T.ease}`).join(', ');

/* verbatim legacy .btn / .buy, off-scale */
const CTA = { font: 14, track: '0.12em', py: 15, px: 28, h: 50, r: 10, gap: 10 };
const BUY = { font: 13, track: '0.14em', py: 14, px: 10, h: 50, r: 10 };
/* legacy .pdp-bar-buy: the compact buy in the PDP sticky bar (size="sm"), 44px. */
const BUY_SM = { py: 10, px: 18, h: 44 };
/* Mono "right arrow" glyph that slides in on the live buy hover (legacy .buy.live::after). */
const ARROW = "'\\2192'";

/* Tones: primary is the amber fill (legacy .btn-solid, .buy.live); neutral is the glass ghost (.btn-ghost) or the
   pending buy (.buy.pending). The live hover out-ranks :active, as legacy (.buy.live:hover beats .buy:active). */
export function shopButtonCss(cta: string, buy: string): string {
  const ctaPri = `${cta}[data-rv-tone="primary"]`;
  const ctaNeu = `${cta}[data-rv-tone="neutral"]`;
  const buyPri = `${buy}[data-rv-tone="primary"]`;
  return `${cta} { position: relative; overflow: hidden; display: inline-flex; align-items: center; justify-content: center; gap: ${CTA.gap}px; font-family: ${T.mono}; font-size: ${CTA.font}px; letter-spacing: ${CTA.track}; text-transform: uppercase; white-space: nowrap; padding: ${CTA.py}px ${CTA.px}px; border-radius: ${CTA.r}px; border: 1px solid transparent; min-height: ${CTA.h}px; text-decoration: none; cursor: pointer; transition: ${ms(['transform', 'box-shadow', 'background', 'border-color'])}; }
${ctaPri} { background: ${amberFill}; color: ${T.onGold}; font-weight: 600; box-shadow: inset 0 1px 0 ${C.gloss}, ${C.shadow}; }
${ctaPri}::after { content: ''; position: absolute; top: 0; bottom: 0; left: -60%; width: 40%; background: linear-gradient(105deg, transparent, ${C.sheen}, transparent); transform: skewX(-18deg); transition: left 0.6s ${T.ease}; }
${ctaPri}:hover { transform: translateY(-2px); box-shadow: inset 0 1px 0 ${C.gloss}, ${C.hoverGlow}; }
${ctaPri}:hover::after { left: 120%; }
${ctaNeu} { border-color: ${T.separatorBold}; color: ${T.label}; background: ${C.ghost}; -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px); }
${ctaNeu}:hover { background: ${T.surfaceAlt}; border-color: ${T.accent}; transform: translateY(-2px); }
${buy} { font-family: ${T.mono}; font-size: ${BUY.font}px; letter-spacing: ${BUY.track}; text-transform: uppercase; text-align: center; border-radius: ${BUY.r}px; padding: ${BUY.py}px ${BUY.px}px; min-height: ${BUY.h}px; display: flex; align-items: center; justify-content: center; border: 1px solid ${T.separatorBold}; background: ${T.surfaceAlt}; color: ${T.label}; text-decoration: none; cursor: pointer; transition: ${ms(['background', 'color', 'box-shadow', 'transform'])}; }
${buy}[data-rv-size="sm"] { min-height: ${BUY_SM.h}px; padding: ${BUY_SM.py}px ${BUY_SM.px}px; }
${buyPri} { background: ${amberFill}; border-color: ${T.amber}; color: ${T.onGold}; font-weight: 600; box-shadow: inset 0 1px 0 ${C.buyGloss}; }
${buyPri}:hover { box-shadow: inset 0 1px 0 ${C.buyGloss}, ${C.buyGlow}; transform: translateY(-1px); }
${buy}[data-rv-tone="neutral"] { color: ${T.labelSec}; cursor: default; }
${buy}:active { transform: translateY(1px) scale(0.995); }
${buyPri}::after { content: ${ARROW}; display: inline-block; margin-left: 8px; opacity: 0; transform: translateX(-8px); transition: ${ms(['opacity', 'transform'])}; }
${buyPri}:hover::after { opacity: 1; transform: none; }
@media (prefers-reduced-motion: reduce) { ${ctaPri}::after { animation: none; transition: none; } }`;
}
